import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../config/environment';
import { toast } from 'react-toastify';

// Global flag to prevent multiple session expired events from being dispatched
let sessionExpiredEventDispatched = false;

// Create axios instance with default config
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include credentials for session-based auth
});

/**
 * Get the base URL for building full URLs (e.g., for iframes, downloads)
 * Use this when you need to construct a full URL but not make an API call through apiClient
 */
export const getBaseUrl = (): string => {
  return API_BASE_URL;
};

// Request interceptor (no need to add authorization headers for session-based auth)
axiosInstance.interceptors.request.use(
  (config) => {
    // Session-based authentication uses cookies automatically
    // No need to add Authorization headers
    
    // Fix duplicate /api in URL path (e.g., /api/api/v2/... -> /api/v2/...)
    if (config.url && config.url.includes('/api/api/')) {
      config.url = config.url.replace(/\/api\/api\//g, '/api/');
      console.warn(`[apiClient] Fixed duplicate /api in URL: ${config.url}`);
    }
    
    console.log(`[apiClient] Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('[apiClient] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Check if we should skip interceptor (e.g., for magic link verification)
    const skipInterceptor = error.config?.headers?.['X-Skip-Interceptor'] === 'true';
    
    if (skipInterceptor) {
      console.log('[apiClient] Skipping error interceptor as requested');
      return Promise.reject(error);
    }
    
    // Handle network errors (no response received)
    if (!error.response) {
      console.error('❌ [apiClient] Network error - no response received:', error.message);
      toast.error('Network Error - Unable to connect to server', {
        autoClose: 5000,
        position: 'top-center',
        toastId: 'network-error', // Prevent duplicate toasts
      });
      return Promise.reject(error);
    }
    
    // Handle 401 Unauthorized - Session expired or invalid
    if (error.response?.status === 401) {
      console.error('❌ [apiClient] 401 Unauthorized - Session expired or invalid');
      
      // Only dispatch event and show toast ONCE per session expiration
      if (!sessionExpiredEventDispatched) {
        sessionExpiredEventDispatched = true;
        
        // Show toast notification
        toast.error('Session expired - Please log in again', {
          autoClose: 3000,
          position: 'top-center',
          toastId: 'unauthorized-401', // Prevent duplicate toasts
        });
        
        // Dispatch event to trigger session renewal/redirect
        console.log('📢 [apiClient] Dispatching auth-session-expired event (401)');
        window.dispatchEvent(new CustomEvent('auth-session-expired', { 
          detail: { status: 401, message: 'Session expired' }
        }));
        
        // Reset flag after 5 seconds (in case user doesn't navigate away)
        setTimeout(() => { 
          sessionExpiredEventDispatched = false; 
          console.log('[apiClient] Reset sessionExpiredEventDispatched flag');
        }, 5000);
      } else {
        console.log('[apiClient] Session expired event already dispatched, skipping...');
      }
      
      // Reject error
      return Promise.reject(error);
    }
    
    // Handle 403 Forbidden - Could be session missing or access denied
    if (error.response?.status === 403) {
      console.error('❌ [apiClient] 403 Forbidden - Access denied or session missing');
      
      // Check if it's a session issue (common response from backend)
      const errorData = error.response?.data;
      if (errorData?.error === 'session_missing' || errorData?.error === 'forbidden') {
        console.log('[apiClient] Session missing detected');
        
        // Only dispatch event and show toast ONCE per session expiration
        if (!sessionExpiredEventDispatched) {
          sessionExpiredEventDispatched = true;
          
          // Show toast notification
          toast.error('Session expired - Please log in again', {
            autoClose: 3000,
            position: 'top-center',
            toastId: 'forbidden-403-session', // Prevent duplicate toasts
          });
          
          // Dispatch event to trigger session renewal/redirect
          console.log('📢 [apiClient] Dispatching auth-session-expired event (403)');
          window.dispatchEvent(new CustomEvent('auth-session-expired', { 
            detail: { status: 403, message: 'Session missing or forbidden' }
          }));
          
          // Reset flag after 5 seconds (in case user doesn't navigate away)
          setTimeout(() => { 
            sessionExpiredEventDispatched = false; 
            console.log('[apiClient] Reset sessionExpiredEventDispatched flag');
          }, 5000);
        } else {
          console.log('[apiClient] Session expired event already dispatched, skipping...');
        }
        
        // Reject error
        return Promise.reject(error);
      }
      
      // Otherwise, it's a real permission issue - show toast but don't redirect
      toast.warning('Access denied - Insufficient permissions', {
        autoClose: 4000,
        position: 'top-center',
        toastId: 'forbidden-403-permission', // Prevent duplicate toasts
      });
      
      // Reject error without redirect
      return Promise.reject(error);
    }
    
    // Handle all other API errors (4xx, 5xx) with Toast notifications
    const status = error.response?.status;
    const errorData = error.response?.data;
    const errorMessage = errorData?.detail || errorData?.message || error.message || 'An error occurred';
    
    // Show toast for all other errors
    if (status && status >= 400) {
      let toastMessage = '';
      
      if (status >= 500) {
        // Server errors (500+)
        const errorId = errorData?.error_id || 'unknown';
        toastMessage = `Server Error (${status}): ${errorMessage}\nError ID: ${errorId}`;
        toast.error(toastMessage, {
          autoClose: 5000,
          position: 'top-right',
          style: {
            whiteSpace: 'pre-line'
          }
        });
      } else if (status === 400) {
        // Bad Request
        toastMessage = `Invalid Request: ${errorMessage}`;
        toast.warning(toastMessage, {
          autoClose: 4000,
          position: 'top-right',
        });
      } else if (status === 404) {
        // Not Found
        toastMessage = `Not Found: ${errorMessage}`;
        toast.info(toastMessage, {
          autoClose: 3000,
          position: 'top-right',
        });
      } else if (status === 422) {
        // Validation Error
        toastMessage = `Validation Error: ${errorMessage}`;
        toast.error(toastMessage, {
          autoClose: 5000,
          position: 'top-right',
        });
      } else if (status >= 400 && status < 500) {
        // Other client errors
        toastMessage = `Error (${status}): ${errorMessage}`;
        toast.error(toastMessage, {
          autoClose: 4000,
          position: 'top-right',
        });
      }
      
      console.error(`[apiClient] Error ${status}:`, errorMessage);
    }
    
    return Promise.reject(error);
  }
);

class ApiClient {
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.get(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.post(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.put(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.patch(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return axiosInstance.delete(url, config);
  }
}

export const apiClient = new ApiClient();
export default apiClient;
